import { infiniteConfigSetting } from '@/api/api'
import { k6api } from '@/api/k6-api'
import { FetchKolContentPayload } from '@/api/workspaces-api'
import { SimilarKols } from '@/store/kol-detail'
import { Platform } from '@/tests/mock-data/mock-content-distribution'
import { FetchKolContentResponse } from '@/types/api/kols'
import { FetchRelatedKolListRequest } from '@/types/api/related-kol-list'
import { ListResource } from '@/types/resource-type'
import {
  RelatedKolList,
  relatedKolListSchema,
} from '@/types/schema/kol-detail-api/related-kol'
import { camelizeKeys } from '@/utils/humps'
import removeNil from '@/utils/remove-nil'

interface BasicRequest {
  uuid: string
}

interface FetchKolsContentRequest extends FetchKolContentPayload {
  uuid: string
}

interface FetchSimilarKolsRequest extends BasicRequest {
  platform: string
  page?: number
  per_page?: number
}

const k6KolsApi = k6api.injectEndpoints({
  endpoints: (builder) => ({
    fetchInfiniteKolsContent: builder.query<
      FetchKolContentResponse,
      FetchKolsContentRequest
    >({
      query: ({ uuid, ...payload }) => {
        const params = removeNil(payload)

        return {
          url: `/kols/${uuid}/content`,
          params,
        }
      },
      ...infiniteConfigSetting<
        FetchKolsContentRequest,
        FetchKolContentResponse
      >(),
      transformResponse: (response: object) => {
        return camelizeKeys(response) as FetchKolContentResponse
      },
    }),
    fetchKolsSimilarKols: builder.query<
      ListResource<SimilarKols>,
      FetchSimilarKolsRequest
    >({
      query: ({ uuid, platform, per_page = 10, page = 1 }) => {
        return {
          url: `/kols/${uuid}/similar-kols`,
          params: { platform, per_page, page },
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as ListResource<SimilarKols>
      },
    }),
    fetchKolsRelatedKols: builder.query<
      RelatedKolList,
      FetchRelatedKolListRequest
    >({
      query: ({ kolId, ...params }) => {
        return {
          url: `/kols/${kolId}/related-kols`,
          params,
        }
      },
      extraOptions: {
        dataSchema: relatedKolListSchema,
      },
    }),
    fetchKolsKolLinkAnalysis: builder.query<Platform, BasicRequest>({
      query: ({ uuid }) => {
        return {
          url: `/kols/${uuid}/link-analytics`,
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as Platform
      },
    }),
  }),
  overrideExisting: false,
})

export const useFetchInfiniteKolsContentQuery =
  k6KolsApi.endpoints.fetchInfiniteKolsContent.useQuery
export const useFetchKolsSimilarKolsQuery =
  k6KolsApi.endpoints.fetchKolsSimilarKols.useQuery
export const useFetchKolsKolLinkAnalysisQuery =
  k6KolsApi.endpoints.fetchKolsKolLinkAnalysis.useQuery
export const useFetchKolsRelatedKolsQuery =
  k6KolsApi.endpoints.fetchKolsRelatedKols.useQuery
